<template>
  <span data-dd-privacy="mask">
    <span
      class="badge rounded-pill client-badge"
      :style="getStyle"
      :title="getClientTitle"
    >
      <font-awesome-icon
        v-if="supervisionHours !== false"
        class="supervision"
        icon="eye"
        :title="`Supervision ${supervisionHours}`"
      />
      {{ fetchedClient.initials }}
      <font-awesome-icon v-if="isArchived()" icon="user-slash" size="xs" />
      <client-escalation-level
        v-if="showEscalationLevel"
        :client="fetchedClient"
        :hide-if-level0="true"
        :show-escalation-date="false"
        :date="dateContext"
      />
      <template v-if="shouldShowRoomNumber">
        -
        <span>{{ getRoomNumber }}</span>
      </template>
    </span>
    <template v-if="shouldShowRoomNumber">
      <span v-if="showLocation" class="ms-1">{{ getLocationName }} </span>
    </template>
    <template v-else>
      <span v-if="showLocation">
        <span class="ms-1">
          Room {{ getRoomNumber }},
          {{ getLocationName }}
        </span>
      </span>
    </template>
    <span
      v-if="showLargeArchivedBadge && isArchived()"
      class="badge rounded-pill client-badge bg-secondary ms-1"
      :title="`This record was archived on ${getArchivedDateString()}`"
    >
      Archived
    </span>
  </span>
</template>

<script>
import { computed, ref, toRaw } from "vue";
import { mapGetters, useStore } from "vuex";
import find from "lodash/find";
import get from "lodash/get";
import has from "lodash/has";
import { getColour } from "@switchsystems/coactive-vue-lib";
import { formatDateForUkFormat } from "../../lib/date-functions";
import {
  getClientHourDefinitionsForDate,
  clientIsConfusable,
} from "../../lib/client-functions";
import ClientEscalationLevel from "./ClientEscalationLevel.vue";

export default {
  name: "ClientBadge",
  components: {
    ClientEscalationLevel,
  },
  props: {
    client: {
      type: Object,
      default() {
        return {
          initials: "",
          location: {
            locationId: "",
            roomNumber: "",
          },
        };
      },
    },
    showLocation: {
      type: Boolean,
      default() {
        return false;
      },
    },
    showLargeArchivedBadge: {
      type: Boolean,
      default() {
        return false;
      },
    },
    showEscalationLevel: {
      type: Boolean,
      default() {
        return true;
      },
    },
    showRoomNumber: {
      type: [Boolean, String],
      required: false,
      default: () => "auto",
    },
    noBackgroundColour: {
      type: Boolean,
      default() {
        return false;
      },
    },
    allClients: {
      // performance helper so that we don't have to call the client store ourselves
      // when we're rendering lots of client badges
      // note: semi defunct now we have a client cache
      type: Array,
      required: false,
      default: () => [],
    },
    dateContext: {
      type: Date,
      default: () => new Date(),
    },
  },
  setup(props) {
    const store = useStore();
    // if we're given allClients, then we don't necessarily need to load them from the store
    const clients = ref(props.allClients);
    if (props.allClients.length === 0) {
      store.dispatch("clients/getAllClients").then((data) => {
        if (data !== false) {
          clients.value = data;
        }
      });
    }

    const fetchedClient = computed(() => {
      const clientId = props.client.clientId ?? props.client.id;
      if (clientId === undefined) {
        return props.client;
      }
      const client = clients.value.find((a) => a.id === clientId);
      if (client) {
        return client;
      }
      return props.client;
    });

    const backgroundColor = computed(() => {
      const seed = `${fetchedClient.value.initials}/${fetchedClient.value.location?.locationId}-${fetchedClient.value.location?.roomNumber}`;
      return getColour(seed);
    });

    const supervisionHours = computed(() => {
      const clientId = fetchedClient.value.clientId ?? fetchedClient.value.id;
      if (clientId === undefined) {
        return false;
      }
      // search clients
      const client = clients.value.find((a) => a.id === clientId);
      if (!client) {
        return false;
      }
      const hours = getClientHourDefinitionsForDate(client, props.dateContext);
      if (get(hours, "supervision", []).length > 0) {
        return hours.supervision
          .map((cur) => `${cur.startTime} - ${cur.endTime}`)
          .join(", ");
      }

      return false;
    });

    /**
     * if we have a truthy shouldShowRoomNumber then use that, otherwise
     * create a computed value which lookups up whether the client is confusable or not
     */
    let shouldShowRoomNumber;
    if (props.showRoomNumber === false) {
      shouldShowRoomNumber = false;
    } else if (props.showRoomNumber === true) {
      shouldShowRoomNumber = true;
    } else if (props.showRoomNumber === "auto") {
      shouldShowRoomNumber = computed(() =>
        clientIsConfusable(toRaw(fetchedClient.value), clients.value),
      );
    }

    return {
      backgroundColor,
      shouldShowRoomNumber,
      supervisionHours,
      fetchedClient,
    };
  },
  computed: {
    ...mapGetters({
      locations: "locations",
    }),
    getClientTitle() {
      let result = `${this.fetchedClient.initials} / Room ${this.getRoomNumber}, ${this.getLocationName}`;
      if (this.isArchived()) {
        result += `\n(Archived on ${this.getArchivedDateString()})`;
      }
      return result;
    },
    getLocationName() {
      return get(
        find(this.locations, {
          value: this.fetchedClient.location?.locationId,
        }),
        "name",
        "",
      );
    },
    getRoomNumber() {
      // is the room number enclosed in quotes?
      if (this.fetchedClient.location?.roomNumber.match(/^".*"$/)) {
        return this.fetchedClient.location?.roomNumber.substring(
          1,
          this.fetchedClient.location?.roomNumber.length - 1,
        );
      }
      return this.fetchedClient.location?.roomNumber;
    },
    getStyle() {
      if (this.noBackgroundColour) {
        return "";
      }
      return { "background-color": this.backgroundColor };
    },
  },
  methods: {
    isArchived() {
      return (
        has(this.fetchedClient, "archivedOn") &&
        this.fetchedClient.archivedOn !== undefined
      );
    },
    getArchivedDateString() {
      return formatDateForUkFormat(this.fetchedClient.archivedOn);
    },
  },
};
</script>
